/**
 * SubscriptionContext
 *
 * Single source of truth for subscription state across the portal. Replaces the
 * per-component RC fetch pattern that SubscriptionGate used to do in its own
 * useEffect. After Phase 1 of the freemium redesign, many components (every
 * paid-action button via useSubscriptionGuard) need to read isPaid; without a
 * shared context each would re-fetch.
 *
 * Also owns:
 *  - Post-checkout polling: if the URL has ?checkout_success=true, refetch
 *    every 2s for up to 30s until isPaid flips, then strip the URL param.
 *    Handles eventual-consistency between Stripe redirect (immediate) and
 *    webhook firing (a few seconds delayed).
 *  - Paywall modal state: modalAction string + dismissModal. useSubscriptionGuard
 *    sets the action when a free user clicks a paid affordance; the single
 *    <UpgradeModal /> mounted at App.tsx root renders it.
 *
 * Spec: /web/ONBOARDING_FREEMIUM_REDESIGN_PLAN.md §8 Phase 1 B1.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { revenueCatAPI } from '../services/api';
import analytics from '../services/analytics';
import { socketService } from '../services/socketService';

interface SubscriptionState {
  // True iff the requesting user's family currently has paid access (paid or
  // cancelled-but-still-inside-paid-window). Mirrors the canonical isPaid
  // predicate the backend uses for coach-contact redaction.
  isPaid: boolean;
  isCanceled: boolean;
  expirationDate: string | null;
  store: string | null;
  // Has the first fetch completed? Components can show a loading skeleton
  // while this is false rather than flashing "free" UI for paid users.
  isLoaded: boolean;
  // True while a post-checkout poll is in progress.
  pollingAfterCheckout: boolean;
}

interface SubscriptionContextValue extends SubscriptionState {
  refetch: () => Promise<void>;
  // Modal control for action-level paywall (consumed by useSubscriptionGuard
  // and the single <UpgradeModal /> mounted at App.tsx root).
  modalAction: string | null;
  setModalAction: (action: string | null) => void;
  dismissModal: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { sessionToken, user, isLoading: authLoading } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    isPaid: false,
    isCanceled: false,
    expirationDate: null,
    store: null,
    isLoaded: false,
    pollingAfterCheckout: false,
  });
  const [modalAction, setModalAction] = useState<string | null>(null);
  // Paywall funnel instrumentation (P2.3). Track when the modal opens and
  // how long it stays open before dismissal — distinct from the converted
  // event which fires server-side on Stripe webhook completion.
  const modalOpenedAtRef = useRef<number | null>(null);

  // Track the live token + active poll loop so cleanup runs cleanly on unmount
  // or session change.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!sessionToken) {
      setState((s) => ({ ...s, isLoaded: true }));
      return false;
    }
    /*
     * A college coach is not a paying family and no /coach route is gated on
     * RevenueCat, so asking about their subscription is asking a question with
     * no answer: GET /api/revenuecat/status 404s on every coach page load and
     * logs a red console error over a paid product, burying anything real a
     * coach might be asked to screenshot.
     */
    // Staff users bypass subscription gating entirely. Mirrors the bypass at
    // SubscriptionGate.tsx:146-149 and the backend redaction helper.
    if (user?.user_type === 'staff' || user?.user_type === 'coach') {
      setState({
        isPaid: true,
        isCanceled: false,
        expirationDate: null,
        store: null,
        isLoaded: true,
        pollingAfterCheckout: false,
      });
      return true;
    }
    try {
      const status = await revenueCatAPI.getSubscriptionStatus(sessionToken);
      const isCanceled =
        !!status?.isActive && !status?.willRenew && !!status?.expirationDate;
      const isPaid = !!status?.isActive;
      setState((s) => ({
        ...s,
        isPaid,
        isCanceled,
        expirationDate: status?.expirationDate ?? null,
        store: status?.store ?? null,
        isLoaded: true,
      }));
      return isPaid;
    } catch (err) {
      console.error('SubscriptionContext: failed to fetch RC status:', err);
      setState((s) => ({ ...s, isLoaded: true }));
      return false;
    }
  }, [sessionToken, user]);

  // First fetch on mount / auth change.
  useEffect(() => {
    if (authLoading) return;
    fetchStatus();
  }, [authLoading, fetchStatus]);

  // Real-time refresh: backend emits `subscription:updated` to user:${id}
  // whenever family.subscription_status flips (Stripe webhook, RC webhook,
  // promo redeem, etc.). Without this the locks linger until a hard refresh
  // or re-login — the exact UX bug Walter hit on staging.
  useEffect(() => {
    if (!sessionToken) return;
    const handler = () => { fetchStatus(); };
    socketService.subscribe('subscription:updated', handler);
    return () => socketService.unsubscribe('subscription:updated', handler);
  }, [sessionToken, fetchStatus]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setState((s) => ({ ...s, pollingAfterCheckout: false }));
  }, []);

  // Post-checkout polling: on first paint, if URL has ?checkout_success=true,
  // poll until isPaid flips (or 30s timeout). Webhook usually fires within
  // a few seconds of Stripe redirect; without this the dashboard shows stale
  // "free" state until a manual refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout_success') !== 'true') return;
    if (!sessionToken) return;

    setState((s) => ({ ...s, pollingAfterCheckout: true }));

    const cleanupParam = () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('checkout_success');
        url.searchParams.delete('session_id');
        const newPath =
          url.pathname + (url.search ? url.search : '') + url.hash;
        window.history.replaceState({}, '', newPath);
      } catch {
        /* never break render on URL cleanup */
      }
    };

    pollIntervalRef.current = setInterval(async () => {
      const paid = await fetchStatus();
      if (paid) {
        stopPolling();
        cleanupParam();
      }
    }, POLL_INTERVAL_MS);

    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      cleanupParam();
    }, POLL_TIMEOUT_MS);

    return () => {
      stopPolling();
    };
    // We deliberately depend only on sessionToken — re-running on every fetchStatus
    // identity change would re-trigger polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  // P2.3 wrapper around the raw setter — fires paywall_shown when an action
  // is set (going from null/different action → an action) and resets the
  // open-timer so paywall_dismissed can compute a duration.
  const setModalActionInstrumented = useCallback((action: string | null) => {
    setModalAction((prev) => {
      if (action && action !== prev) {
        modalOpenedAtRef.current = Date.now();
        try { analytics.track('paywall_shown', { trigger_action: action }); } catch {}
      }
      return action;
    });
  }, []);

  const dismissModal = useCallback(() => {
    setModalAction((prev) => {
      if (prev && modalOpenedAtRef.current) {
        const openMs = Date.now() - modalOpenedAtRef.current;
        try { analytics.track('paywall_dismissed', { trigger_action: prev, time_open_ms: openMs }); } catch {}
      }
      modalOpenedAtRef.current = null;
      return null;
    });
  }, []);

  const value: SubscriptionContextValue = {
    ...state,
    refetch: async () => {
      await fetchStatus();
    },
    modalAction,
    setModalAction: setModalActionInstrumented,
    dismissModal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error(
      'useSubscription must be used inside a <SubscriptionProvider>'
    );
  }
  return ctx;
}
